import { QueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { GetServerSideProps } from 'next';

import { CheckoutCanceledModal } from '../../../components/_molecules/CheckoutCanceledModal/CheckoutCanceledModal';
import PricingPageContent from '../../../components/_organisms/PricingPageContent';
import { PricingPageHeader } from '../../../components/_organisms/PricingPageContent/PricingPageHeader';
import { NextPageWithLayout } from '../../../components/layouts';
import LandingPageLayout from '../../../components/layouts/LandingPageLayout';
import { useLocale } from '../../../hooks/useLocale';
import { getLocaleProps } from '../../../utils/localisationUtils';
import { getReadOnlyServerSession } from '../../../utils/serverSession';
import { provisionFlags } from '../../../utils/ServerSideFeatureFlagUtils';

interface LocalizedPricingProps {
  locale: string;
}

const LocalizedPricing: NextPageWithLayout<LocalizedPricingProps> = ({ locale }) => {
  const { t } = useTranslate();
  useLocale();

  // Language is set via SSR prop on TolgeeProvider, no need to set it manually

  // Get localized metadata using Tolgee
  const title = t('meta.pricing.title', 'Curipod Pricing - Plans for Educators');
  const description = t(
    'meta.pricing.description',
    'Explore our pricing details for educators bringing curiosity into the classroom and save time, spark curiosity, and increase engagement with interactive lessons students love.',
  );
  // Paused until decision on product event
  // useEffect(() => {
  //   // Track pricing page views for target roles
  //   const targetRoles = [
  //     'Instructional/Tech Coach',
  //     'School Administrator',
  //     'District Administrator',
  //     'Purchasing Manager',
  //     'adminDistrict',
  //   ];

  //   const userRole = accessBadge?.user?.profileInfo?.role;
  //   const organizationId = accessBadge?.organization?.id;
  //   const email = accessBadge?.user?.email;
  //   const userId = accessBadge?.user?.id;

  //   if (userRole && targetRoles.includes(userRole) && organizationId && userId && email) {
  //     trackAttioEvent('admin_pricing_page_view', {
  //       organizationId,
  //       userRole,
  //       userId,
  //       email,
  //       firstName: accessBadge?.user?.firstName,
  //       lastName: accessBadge?.user?.lastName,
  //       locale, // Track which locale was viewed
  //     });
  //   }
  // }, [accessBadge, trackAttioEvent, locale]);

  return (
    <>
      <CheckoutCanceledModal />
      <LandingPageLayout
        title={title} // SSR-provided, available immediately
        subTitle={description} // SSR-provided, available immediately
        locale={locale}
        hideBlobs={false}
        disableHeadline={true}
        customHeader={<PricingPageHeader />}
      >
        <PricingPageContent />
      </LandingPageLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const result = await getLocaleProps(context);
  // getLocaleProps can short-circuit with `notFound`/`redirect` for an unsupported
  // locale — only merge flag provisioning into the success path.
  if (!('props' in result)) return result;

  // Read-only: public marketing page; the session only seeds flag targeting.
  const session = await getReadOnlyServerSession(context.req);
  const queryClient = new QueryClient();
  // Provision flags server-side so the client Unleash instance bootstraps with the
  // correct values on the very first render — see pages/signin.tsx.
  const flags = await provisionFlags(context, queryClient, session);

  return { ...result, props: { ...result.props, ...flags } };
};

LocalizedPricing.getLayout = function getLayout(page) {
  return <>{page}</>;
};

export default LocalizedPricing;
